import type { FloatingUIOptions } from '@blocknote/react';
import {
  autoUpdate,
  flip,
  offset,
  shift,
  size,
  type Middleware,
} from '@floating-ui/react';

/** Padding between the slash menu and the viewport edge. */
const VIEWPORT_PADDING = 12;

/** Default menu height (~6–7 items); shrinks only if the viewport is smaller. */
export const SLASH_MENU_MIN_HEIGHT_PX = 240;

/** Cap height; list scrolls inside the menu when there are more items. */
export const SLASH_MENU_MAX_HEIGHT_PX = 360;

const SLASH_MENU_OFFSET_PX = 6;

/** Portal out of the scrollable page column so `fixed` uses the real viewport. */
export const slashMenuPortalElement =
  typeof document !== 'undefined' ? document.body : null;

/** Apply min height before flip measures, so flip knows the menu does not fit below. */
function slashMenuPresetSize(): Middleware {
  return {
    name: 'slashMenuPresetSize',
    fn({ elements }) {
      elements.floating.style.boxSizing = 'border-box';
      elements.floating.style.display = 'flex';
      elements.floating.style.flexDirection = 'column';
      elements.floating.style.overflow = 'hidden';
      elements.floating.style.minHeight = `${SLASH_MENU_MIN_HEIGHT_PX}px`;
      return {};
    },
  };
}

/**
 * Flip runs before size() applies min-height, so it can still pick "below" for a
 * one-line-tall menu. Force the menu fully on-screen using the real minimum height.
 */
function slashMenuKeepOnScreen(): Middleware {
  return {
    name: 'slashMenuKeepOnScreen',
    fn({ y, rects }) {
      const vh = window.innerHeight;
      const menuH = Math.max(rects.floating.height, SLASH_MENU_MIN_HEIGHT_PX);
      const ref = rects.reference;
      const gap = SLASH_MENU_OFFSET_PX;
      let nextY = y;

      if (nextY + menuH > vh - VIEWPORT_PADDING) {
        const aboveY = ref.y - menuH - gap;
        if (aboveY >= VIEWPORT_PADDING) {
          nextY = aboveY;
        } else {
          nextY = Math.max(VIEWPORT_PADDING, vh - VIEWPORT_PADDING - menuH);
        }
      }

      if (nextY < VIEWPORT_PADDING) {
        nextY = VIEWPORT_PADDING;
      }

      return { y: nextY };
    },
  };
}

export const slashMenuFloatingOptions: FloatingUIOptions = {
  useFloatingOptions: {
    strategy: 'fixed',
    placement: 'bottom-start',
    whileElementsMounted: autoUpdate,
    middleware: [
      slashMenuPresetSize(),
      offset(SLASH_MENU_OFFSET_PX),
      flip({
        fallbackPlacements: ['top-start', 'bottom-start', 'top-end', 'bottom-end'],
        padding: VIEWPORT_PADDING,
      }),
      shift({ padding: VIEWPORT_PADDING }),
      slashMenuKeepOnScreen(),
      size({
        padding: VIEWPORT_PADDING,
        apply({ y, elements }) {
          const vh = window.innerHeight;
          const space = Math.max(0, vh - VIEWPORT_PADDING - y);
          const maxH = Math.min(SLASH_MENU_MAX_HEIGHT_PX, space);
          const minH = Math.min(SLASH_MENU_MIN_HEIGHT_PX, maxH);

          elements.floating.style.maxHeight = `${maxH}px`;
          elements.floating.style.minHeight = `${minH}px`;
        },
      }),
    ],
  },
  elementProps: {
    className: 'couplespace-slash-menu-floating',
  },
};
